import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
  DrawerHeader,
  DrawerFooter,
  Icon,
  Heading,
  FormLabel,
  FormControl,
  HStack,
  VStack,
  Input,
  Button,
  FormErrorMessage,
  useToast,
} from '@chakra-ui/react';
import mediaQueryState from '~/recoil/atom/mediaQueryState';
import { useRecoilValue } from 'recoil';

import { IoAlertCircleOutline, IoPersonCircleOutline } from 'react-icons/io5';
import { Controller, useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { emailRegex } from '~/types/utilities';
import updateCustomer from '~/firebase/update/updateCustomer';
import { PLATFORM_ID } from '~/globalVariables';
import { CustomerWithId } from '~/types/platform/customer';

type MemberInfoModalProps = {
  isOpen: boolean;
  onClose: () => void;
  defaultValues: {
    id: CustomerWithId['id'];
    displayName: CustomerWithId['displayName'];
    phoneNumber: CustomerWithId['phoneNumber'];
    /** yyyy-MM-dd */
    birthday: CustomerWithId['birthday'];
    email: CustomerWithId['email'];
  };
};

const ErrorMsg = ({ message }: { message?: string }) => {
  if (!message) return null;
  return (
    <FormErrorMessage
      w="full"
      borderRadius="5px"
      p="3px 5px"
      bg="#FFF5F5"
      color="danger.600"
      fontSize="14px"
      fontWeight={400}
    >
      <Icon as={IoAlertCircleOutline} color="danger.600" boxSize="1.5rem" mr="5px" />
      {message || ''}
    </FormErrorMessage>
  );
};

export default function MemberInfoModal({ isOpen, onClose, defaultValues }: MemberInfoModalProps) {
  const { isLargerThan768 } = useRecoilValue(mediaQueryState);
  const toast = useToast({ duration: 2000, position: 'top' });
  const { control, reset, handleSubmit } = useForm({ defaultValues });

  useEffect(() => {
    const defaultData = {
      ...defaultValues,
      birthday: defaultValues.birthday || '',
    };
    reset(defaultData);
  }, [isOpen]);

  const handleUpdateMemberInfo = handleSubmit(
    async (data) => {
      const { id, ...customerData } = {
        ...data,
        email: data.email || '',
        birthday: data.birthday || '',
      };
      try {
        await updateCustomer(PLATFORM_ID, id, customerData);
        onClose();
        toast({ status: 'success', title: '編輯成功', description: '會員資料已儲存' });
      } catch (err) {
        console.error('update customer error', err);
        toast({ status: 'error', title: '編輯失敗', description: '請重新嘗試' });
      }
    },
    (e) => console.log('e', e),
  );

  if (isLargerThan768) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} scrollBehavior={'inside'}>
        <ModalOverlay />
        <ModalContent my="auto">
          <ModalCloseButton />
          <ModalHeader mt={4} borderBottomWidth="1px" borderColor="primary">
            <HStack justify="center">
              <Icon as={IoPersonCircleOutline} color="primary" boxSize="1.5rem" />
              <Heading size="md" fontWeight="normal">
                個人資訊
              </Heading>
            </HStack>
          </ModalHeader>
          <ModalBody>
            <VStack spacing={4}>
              <Controller
                name="displayName"
                control={control}
                rules={{ required: '此為必填項目' }}
                render={({ field, fieldState }) => {
                  return (
                    <FormControl isRequired isInvalid={fieldState?.invalid}>
                      <FormLabel aria-required fontWeight="normal" fontSize="sm">
                        姓名
                      </FormLabel>
                      <Input {...field} />
                      <ErrorMsg message={fieldState?.error?.message} />
                    </FormControl>
                  );
                }}
              />

              <Controller
                name="phoneNumber"
                control={control}
                render={({ field }) => {
                  return (
                    <FormControl>
                      <FormLabel fontWeight="normal" fontSize="sm">
                        手機
                      </FormLabel>
                      <Input type="tel" inputMode="tel" {...field} />
                    </FormControl>
                  );
                }}
              />

              <Controller
                name="birthday"
                control={control}
                render={({ field }) => {
                  return (
                    <FormControl>
                      <FormLabel fontWeight="normal" fontSize="sm">
                        生日日期
                      </FormLabel>
                      <Input type="date" {...field} value={field.value as string} />
                    </FormControl>
                  );
                }}
              />

              <Controller
                name="email"
                rules={{
                  pattern: {
                    value: emailRegex,
                    message: '格式錯誤',
                  },
                }}
                control={control}
                render={({ field, fieldState }) => {
                  return (
                    <FormControl isInvalid={fieldState?.invalid}>
                      <FormLabel fontWeight="normal" fontSize="sm">
                        電子信箱
                      </FormLabel>
                      <Input placeholder="輸入電子信箱" type="email" inputMode="email" {...field} />
                      <ErrorMsg message={fieldState?.error?.message} />
                    </FormControl>
                  );
                }}
              />
            </VStack>
          </ModalBody>
          <ModalFooter p={0}>
            <Button size="lg" borderRadius="0" isFullWidth onClick={handleUpdateMemberInfo}>
              確認
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }
  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="full">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader mt={4} borderBottomWidth="1px" borderColor="primary">
          <HStack justify="center">
            <Icon as={IoPersonCircleOutline} color="primary" boxSize="1.5rem" />
            <Heading size="md" fontWeight="normal">
              個人資訊
            </Heading>
          </HStack>
        </DrawerHeader>
        <DrawerBody>
          <VStack spacing={4} py={8}>
            <Controller
              name="displayName"
              control={control}
              rules={{ required: '此為必填項目' }}
              render={({ field, fieldState }) => {
                return (
                  <FormControl isRequired isInvalid={fieldState?.invalid}>
                    <FormLabel aria-required fontWeight="normal" fontSize="sm">
                      姓名
                    </FormLabel>
                    <Input {...field} />
                    <ErrorMsg message={fieldState?.error?.message} />
                  </FormControl>
                );
              }}
            />

            <Controller
              name="phoneNumber"
              control={control}
              render={({ field }) => {
                return (
                  <FormControl>
                    <FormLabel fontWeight="normal" fontSize="sm">
                      手機
                    </FormLabel>
                    <Input isDisabled type="tel" inputMode="tel" {...field} />
                  </FormControl>
                );
              }}
            />

            <Controller
              name="birthday"
              control={control}
              render={({ field }) => {
                return (
                  <FormControl>
                    <FormLabel fontWeight="normal" fontSize="sm">
                      生日日期
                    </FormLabel>
                    <Input type="date" {...field} value={(field.value as string) || ''} />
                  </FormControl>
                );
              }}
            />

            <Controller
              name="email"
              rules={{
                pattern: {
                  value: emailRegex,
                  message: '格式錯誤',
                },
              }}
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <FormControl isInvalid={fieldState?.invalid}>
                    <FormLabel fontWeight="normal" fontSize="sm">
                      電子信箱
                    </FormLabel>
                    <Input placeholder="輸入電子信箱" type="email" inputMode="email" {...field} />
                    <ErrorMsg message={fieldState?.error?.message} />
                  </FormControl>
                );
              }}
            />
          </VStack>
        </DrawerBody>
        <ModalFooter p={0}>
          <Button size="lg" borderRadius="0" isFullWidth onClick={handleUpdateMemberInfo}>
            確認
          </Button>
        </ModalFooter>
      </DrawerContent>
    </Drawer>
  );
}
